import axios from 'axios';
import { Moment } from 'moment';
import WorkStatus from '@/dossier/constants/WorkStatus';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { CheckInData } from '@/dossier/models/CheckInData';
import { PickedUpData } from '@/dossier/models/PickedUpData';
import { parseMediaFiles } from '@/general/converters/mediaFileConverters';
import parseUploadOrder from '@/general/converters/uploadOrderConverters';
import MediaFile from '@/general/models/MediaFile';
import UploadOrder from '@/general/models/UploadOrder';
import DmsSyncStatusType from '@/shared/constants/DmsSyncStatusType';
import MediaTargetType from '@/shared/constants/MediaTargetType';
import TagType from '@/shared/constants/TagType';
import { parseTags } from '@/shared/converters/tagConverters';
import Tag from '@/shared/models/Tag';
import { UserId } from '@/shared/models/User';
import { minutesSinceMidnight } from '@/shared/utils/dateTime';
import { present } from '@/shared/utils/typescript';
import ExecutionStatus from '../constants/ExecutionStatus';
import WarrantyStatus from '../constants/WarrantyStatus';
import { parseJobActivities } from '../converters/jobActivityConverters';
import {
  formatExecutionStatusForAdmin,
  formatExecutionStatusForMechanic,
  formatJobApproval,
  formatJobData,
  formatJobPriceData,
  formatJobSeverity,
  formatOrderApproval,
  formatPriceApproval,
  parseJob,
  parseJobs,
  parseWarrantyStatus,
} from '../converters/jobConverters';
import { parseJobMasters } from '../converters/jobMasterConverters';
import { JobId } from '../models/AbstractJob';
import Job, { CreateJobData, JobApprovalData, JobDescriptionData, UpdateExecutionStatusData } from '../models/Job';
import JobActivity from '../models/JobActivity';
import JobMaster from '../models/JobMaster';
import PauseJobData from '../models/PauseJobData';
import { CheckInFormData } from '@/dossier/models/CheckInContext';

export class JobRemovalSyncError extends Error {}

class JobClient {
  async loadAll(dossierId: DossierId): Promise<Array<Job>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobs`);
    return parseJobs(response.data);
  }

  async loadMaster(dossierId: DossierId): Promise<Array<JobMaster>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobsmaster`);
    return parseJobMasters(response.data);
  }

  async getJobById(dossierId: DossierId, jobId: JobId): Promise<Job> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}`);
    return present(parseJob(response.data));
  }

  async loadDetail(jobId: JobId, dossierId: DossierId): Promise<Job> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/details`);
    return present(parseJob(response.data));
  }

  async loadPauseReasons(): Promise<Array<Tag>> {
    const response = await axios.get('/api/mobopro/v1/pausereasons', {
      params: {
        type: TagType.JOB,
      },
    });
    return parseTags(response.data, { isPause: true });
  }

  async loadMedia(jobId: JobId, dossierId: DossierId): Promise<Array<MediaFile>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/media`);
    const items = parseMediaFiles(response.data);
    items.forEach(mf => mf.target = MediaTargetType.JOB);
    return items;
  }

  async loadApprovals(dossierId: DossierId): Promise<Array<Job>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobs/approvals`);
    return parseJobs(response.data);
  }

  async setJobStatus(data: PickedUpData | CheckInData | CheckInFormData, jobs: Array<Job>, dossierId: DossierId, isCheckIn: boolean): Promise<void> {
    await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/jobs/approval`, formatJobApproval(data, jobs, isCheckIn));
  }

  async create(data: CreateJobData, dossierId: DossierId): Promise<JobId> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/jobs`, formatJobData(data));
    return response.data.id;
  }

  async updateTags(jobId: JobId, tags: Array<Tag>, dossierId: DossierId): Promise<Array<Tag>> {
    const data = tags.map((tag) => tag.id);
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/tags`, data);
    return present(parseTags(response.data));
  }

  async updateExecutionStatus(data: UpdateExecutionStatusData, isAdmin: boolean, dossierId: DossierId): Promise<ExecutionStatus> {
    if (isAdmin) {
      const jobId = data.jobs[0].id;
      const response = await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}`, formatExecutionStatusForAdmin(data));
      return present(parseJob(response.data)?.executionStatus);
    } else {
      const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/jobs/executionapproval`, formatExecutionStatusForMechanic(data));
      return parseJobs(response.data)[0].executionStatus;
    }
  }

  async assign(targetId: UserId | null, jobId: JobId, dossierId: DossierId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}`, {
      assignedToId: targetId,
    });
  }

  async updateWorkStatus(workStatus: WorkStatus, jobId: JobId, dossierId: DossierId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}`, { workStatus });
  }

  async pause(data: PauseJobData, jobId: JobId, dossierId: DossierId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}`, {
      workStatus: WorkStatus.ON_HOLD,
      pauseReasonId: data.willContinue ? undefined : data.pauseReason.id,
      isFollowUpAppointmentRequired: data.willContinue ? undefined : data.needFollowUp,
    });
  }

  async updateNeedsFollowUp(id: JobId, needsFollowUp: boolean, dossierId: DossierId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${id}`, {
      IsFollowUpAppointmentRequired: needsFollowUp,
    });
  }

  async updateDescription(data: JobDescriptionData, jobId: JobId, dossierId: DossierId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}`, {
      description: data.description,
      severity: formatJobSeverity(data.severity) ?? undefined,
    });
  }

  async loadActivity(jobId: JobId, dossierId: DossierId): Promise<Array<JobActivity>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/timeline`);
    return parseJobActivities(response.data);
  }

  async getUploadOrder(id: JobId): Promise<UploadOrder> {
    const response = await axios.post('/api/mobopro/v1/pdfuploads/pin', { id, type: 'Job' });
    return present(parseUploadOrder(response.data));
  }

  async updateAllowedDiagnosticTime(id: JobId, time: Moment | null, dossierId: DossierId): Promise<number> {
    const response = await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${id}`, {
      allowedDiagnosticDuration: time ? minutesSinceMidnight(time) : null,
    });
    return response.data.allowedDiagnosticDuration;
  }

  async remove(id: JobId, dossierId: DossierId): Promise<void> {
    const response = await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${id}`);
    if (response.data.dmsSyncStatus === DmsSyncStatusType.SYNCFAILED) throw new JobRemovalSyncError();
  }

  savePriceApprovals(jobs: Array<Job>, data: JobApprovalData, dossierId: DossierId): Promise<void> {
    return this.saveApprovals(jobs.map(formatPriceApproval), data, dossierId);
  }

  saveOrderApprovals(jobs: Array<Job>, data: JobApprovalData, dossierId: DossierId): Promise<void> {
    return this.saveApprovals(jobs.map(formatOrderApproval), data, dossierId);
  }

  private async saveApprovals(jobs: Array<any>, data: JobApprovalData, dossierId: DossierId): Promise<void> {
    await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/jobs/approval`, {
      approvalSource: null,
      signature: data.signature,
      approvalBy: data.approvalBy?.id ?? null,
      jobs,
      setDealerSignature: false,
      hasPriceApprovalPermission: data.hasPriceApprovalPermission,
    });
  }

  async updatePrice(dossierId: DossierId, job: Job): Promise<WarrantyStatus> {
    const response = await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${job.id}`, formatJobPriceData(job));
    return parseWarrantyStatus(response.data.price.type);
  }

  async linkComment(commentId: JobId, jobId: JobId | null, dossierId: DossierId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${commentId}`, { linkedJobId: jobId });
  }
}

export default new JobClient();